
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { Tables } from '@/integrations/supabase/types';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { signUp, signIn, signOut, updateUserProfile, resetPassword, updatePassword } from '@/services/authService';
import { useToast } from '@/hooks/use-toast';
import { setUserContext, clearUserContext } from '@/utils/sentry';
import { analytics } from '@/utils/analytics';
import { tiktok, ttqIdentify } from '@/utils/tiktokPixel';

type Profile = Tables<'profiles'>;

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  isProfessional: boolean;
  fetchProfile: (userId: string) => Promise<void>;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string; role?: string }) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  updateRole: (role: 'user' | 'professional' | 'admin') => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, user, profile, loading, fetchProfile } = useSupabaseAuth();
  const { toast } = useToast();
  const [initialized, setInitialized] = useState(false);

  // Track user context in Sentry when user logs in/out
  useEffect(() => {
    if (user && profile) {
      // Set user context in Sentry for error tracking
      setUserContext({
        id: user.id,
        email: user.email || undefined,
        role: profile.role || undefined,
      });
      // Advanced matching for the TikTok pixel (no-op if pixel not loaded yet,
      // ttq queues the call until the script is ready)
      ttqIdentify({ email: user.email ?? undefined, external_id: user.id });
    } else if (!user) {
      // Clear user context when logged out
      clearUserContext();
    }
  }, [user, profile]);

  useEffect(() => {
    if (!loading && !initialized) {
      setInitialized(true);
    }
  }, [loading]);

  if (!initialized) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Détermine si l'utilisateur a un rôle spécifique
  const isAdmin = profile?.role === 'admin';
  const isProfessional = profile?.role === 'professional';

  const handleSignUp = async (
    email: string, 
    password: string, 
    metadata?: { first_name?: string; last_name?: string; role?: string }
  ) => {
    const { error } = await signUp(email, password, metadata);
    
    if (error) {
      toast({
        title: "Erreur d'inscription",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
    
    // Track successful sign up
    analytics.userSignedUp(metadata?.role || 'user');
    ttqIdentify({ email });
    tiktok.userRegistered();

    toast({
      title: "Inscription réussie",
      description: "Vous êtes maintenant inscrit. Veuillez vérifier votre email pour confirmer votre compte.",
    });
    
    return { error: null };
  };

  const handleSignIn = async (email: string, password: string) => {
    const { error } = await signIn(email, password);

    if (error) {
      toast({
        title: "Erreur de connexion",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }

    // Track successful login
    analytics.userLoggedIn('email');
    ttqIdentify({ email });

    toast({
      title: "Connexion réussie",
      description: "Vous êtes maintenant connecté.",
    });

    return { error: null };
  };

  const handleSignOut = async () => {
    await signOut();
    
    // Track logout
    analytics.userLoggedOut();
    
    toast({
      title: "Déconnexion réussie",
      description: "Vous êtes maintenant déconnecté.",
    });
  };

  const handleUpdateProfile = async (data: Partial<Profile>) => {
    try {
      if (!user) throw new Error("Utilisateur non connecté");

      const { error } = await updateUserProfile(user.id, data);
      if (error) throw error;

      await fetchProfile(user.id);

      toast({
        title: "Profil mis à jour",
        description: "Votre profil a été mis à jour avec succès.",
      });
    } catch (error: any) {
      toast({
        title: "Erreur de mise à jour",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpdateRole = async (role: 'user' | 'professional' | 'admin') => {
    try {
      if (!user) throw new Error("Utilisateur non connecté");

      const { error } = await updateUserProfile(user.id, { role });
      if (error) throw error;

      await fetchProfile(user.id);

      toast({
        title: "Rôle mis à jour",
        description: `Votre rôle a été mis à jour en tant que ${role}.`,
      });
    } catch (error: any) {
      toast({
        title: "Erreur de mise à jour du rôle",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleResetPassword = async (email: string) => {
    const { error } = await resetPassword(email);
    
    if (error) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
    
    return { error: null };
  };

  const handleUpdatePassword = async (newPassword: string) => {
    const { error } = await updatePassword(newPassword);
    
    if (error) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    }
    
    toast({
      title: "Mot de passe mis à jour",
      description: "Votre mot de passe a été mis à jour avec succès.",
    });
    
    return { error: null };
  };

  const value = {
    session,
    user,
    profile,
    loading,
    isAdmin,
    isProfessional,
    fetchProfile,
    signUp: handleSignUp,
    signIn: handleSignIn,
    signOut: handleSignOut,
    updateProfile: handleUpdateProfile,
    updateRole: handleUpdateRole,
    resetPassword: handleResetPassword,
    updatePassword: handleUpdatePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
